import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';

import { getOrdinalDisplay, getPrettyNumber } from '../../Helpers/formatting';
import shopMyLogo from '../../static/images/logos/logo.png';
import PartershipStatusActions from '../PartnershipStatus/PartershipStatusActions';
import { openArtistModal } from '../../Actions/UIActions';

import './MonthlyBrandAnalyticsPromoter.scss';
import { getResizedUploadImageUrl } from '../../Helpers/helpers';
import { getUserTierDisplay } from '../../Helpers/tier_helpers';

const HIDDEN_RANK_STATS = ['promoters'];
const STAT_SUBTITLE_TYPE = {
  volume: 'sum',
  emv: 'sum',
  orders: 'sum',
  clicks: 'sum',
  links: 'sum',
  promoters: null,
  gifting_sent: 'sum',
  gifting_accepted: 'sum',
  gifting_acceptance_rate: 'avg',
  gifting_promoted: 'sum',
  gifting_promotion_rate: 'avg',
  opportunities_sent: 'sum',
  opportunities_accepted: 'sum',
  opportunities_acceptance_rate: 'avg',
  opportunities_spend: 'sum',
  opportunities_volume: 'sum',
  opportunities_emv: 'sum',
  opportunities_roi: 'avg'
};

const MonthlyBrandAnalyticsPromoter = props => {
  const { months, stat, promoter, rank, openArtistModal } = props;
  const { User_id, username, name, image, ...data } = promoter;
  const monthRanks = rankMonths(data, months);

  const toggleArtistModal = () => {
    openArtistModal({ id: User_id, name, sourceForEventing: 'Brand Analytics By Month' });
  };

  const subtitle =
    STAT_SUBTITLE_TYPE[stat.value] === 'sum'
      ? `${formatStatValue(stat.type, _.sum(Object.values(data).filter(i => !_.isNil(i))))} last 6mo`
      : STAT_SUBTITLE_TYPE[stat.value] === 'avg'
      ? `${formatStatValue(stat.type, _.mean(Object.values(data).filter(i => !_.isNil(i))))} avg last 6mo`
      : stat.value === 'promoters'
      ? getUserTierDisplay(data.tier)
      : username;

  const imageUrl = image ? getResizedUploadImageUrl(image, { width: 50, height: 50, fit: 'cover' }) : shopMyLogo;

  return (
    <li
      className='monthly-brand-stats-table-row monthly-brand-stats-table-row-promoter'
      data-amp-track-promoter-rank={rank}
      data-amp-track-promoter-username={username}
    >
      {/* Promoter info */}
      <button className='monthly-brand-stats-table-row-promoter-info' onClick={() => toggleArtistModal(username)}>
        <p className='monthly-brand-stats-table-row-promoter-info-rank'>
          {!rank || HIDDEN_RANK_STATS.includes(stat.value) ? '' : getOrdinalDisplay(rank)}
        </p>
        <div className='monthly-brand-stats-table-row-promoter-info-image'>
          <img src={imageUrl} />
        </div>
        <p className='monthly-brand-stats-table-row-promoter-info-title'>{name}</p>
        <p className='monthly-brand-stats-table-row-promoter-info-subtitle'>{subtitle}</p>
      </button>

      {/* Promoter stats */}
      {months.map(({ value: monthKey }, i) => (
        <p
          key={`${stat.value}-${User_id}-${monthKey}`}
          className={cn(
            'monthly-brand-stats-table-row-cell',
            i === months.length - 1 && 'monthly-brand-stats-table-row-cell--mtd',
            monthKey in monthRanks && `monthly-brand-stats-table-row-cell--rank-${stat.value === 'promoters' ? 0 : monthRanks[monthKey]}`
          )}
        >
          {stat.value === 'promoters' && data[monthKey] ? (
            <FontAwesomeIcon className='monthly-brand-stats-table-row-promoter-check' icon={faCheck} />
          ) : (
            formatStatValue(stat.type, data[monthKey])
          )}
        </p>
      ))}

      {/* Promoter actions */}
      <PartershipStatusActions User_id={User_id} />
    </li>
  );
};

MonthlyBrandAnalyticsPromoter.propTypes = {
  openArtistModal: PropTypes.func.isRequired,
  months: PropTypes.array.isRequired,
  stat: PropTypes.object.isRequired,
  promoter: PropTypes.object.isRequired
};

export default connect(undefined, { openArtistModal })(MonthlyBrandAnalyticsPromoter);

export const rankMonths = (stats, months) =>
  _.chain(stats)
    .toPairs()
    .filter('1')
    .filter(([month]) => month !== _.last(months).value)
    .orderBy('0', 'desc')
    .orderBy('1', 'desc')
    .map(([key], i) => [key, i])
    .fromPairs()
    .value();

export const formatStatValue = (type, value) => {
  let formattedValue;
  if (value === 0 && type === 'percentage') formattedValue = '0%';
  else if (!value) formattedValue = '-';
  else if (type === 'scalar') formattedValue = getPrettyNumber(value, { precision: 0 });
  else if (type === 'decimal') formattedValue = value.toFixed(1);
  else if (type === 'currency') formattedValue = `$${getPrettyNumber(value, { precision: 0 })}`;
  else if (type === 'percentage') formattedValue = `${getPrettyNumber(value, { precision: 0 })}%`;
  return formattedValue;
};
